import React from 'react';
import { connect } from 'react-redux';

import Drawer from '@material-ui/core/Drawer';
import { withStyles } from '@material-ui/core/styles';

import { isFilterInStrings } from 'helpers/StringHelper.js';

import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import TemplateDrawer from 'components/landlord_app/inspections/templates/TemplateDrawer';
import { AddTemplateDialog } from 'components/landlord_app/inspections/templates/AddTemplateDialog';

import {
  createOrUpdateTemplate,
  getInspectionTemplates,
  getFilteredInspectionTemplates,
  getInspectionTemplatesStats,
} from 'actions/landlordInspectionsActions';
import { getProperties } from 'actions/landlordPropertiesActions';
import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import TableFilters from 'components/ui/TableFilters';
import MediaQuery from 'react-responsive';
import moment from 'moment-timezone';
import { getTranslatedString } from 'helpers/i18n';

const StyledDrawer = withStyles({
  paperAnchorLeft: { maxWidth: '550px', width: '100% !important' },
})(Drawer);

const inspectionTypes = ['property', 'unit', 'turnover'];

export default connect(
  (store) => ({
    templates: store.landlordInspections.templates,
    templatesCounts: store.landlordInspections.templatesCounts,
    templatesSearchResultsCount: store.landlordInspections.templatesSearchResultsCount,
    fetchingInspectionTemplates: store.landlordInspections.fetchingInspectionTemplates,
    upsertingTemplate: store.landlordInspections.upsertingTemplate,
  }),
  {
    createOrUpdateTemplate,
    getInspectionTemplates,
    getFilteredInspectionTemplates,
    getInspectionTemplatesStats,
    getProperties,
  }
)(
  class Templates extends React.Component {
    state = {
      typeFilter: '',
      drawerOpen: false,
      selectedTemplate: {},
      filtersActive: false,
      filters: {
        search: '',
        inspectionTypes: Array(inspectionTypes.length).fill(false),
      },
      currentTab: 'active',
      sortKey: '',
      sortOrder: '',
      offset: 0,
    };

    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    componentDidMount = () => {
      this.props.getInspectionTemplatesStats();
      this.fetchTemplates();
      this.props.getProperties({ for_multiselect: true });
    };

    addTemplate = () => {
      this.openTemplate({
        inspection_rooms: [{ inspects: [{}] }],
        self_guided_type: 'single_lease',
      });
    };

    toggleDrawer = () => {
      this.setState((prevState) => ({ drawerOpen: !prevState.drawerOpen }));
    };

    openTemplate = (selectedTemplate) => {
      this.setState({ selectedTemplate }, this.toggleDrawer);
    };

    setTypeFilter = (e) => {
      this.setState({ typeFilter: e.target.value });
    };

    filteredData = () =>
      this.props.templates.filter((item) =>
        isFilterInStrings([item.name, item.property, item.property_name], this.state.typeFilter)
      );

    filterInspectionTypes = (booleanArrayMask) => {
      return inspectionTypes.filter((_, i) => booleanArrayMask[i]);
    };

    fetchTemplates = () => {
      const { currentTab, filters, filtersActive, offset, sortKey, sortOrder } = this.state;

      let filterOptions = {
        sort_key: sortKey,
        sort_order: sortOrder,
      };

      let subset = currentTab;

      if (filtersActive) {
        const inspectionTypesToFilter = this.filterInspectionTypes(filters.inspectionTypes);

        filterOptions = {
          ...filterOptions,
          query: filters.search,
          inspection_types: inspectionTypesToFilter,
        };
        subset = 'all';
      }

      this.props.getFilteredInspectionTemplates(subset, offset, filterOptions).then((res) =>
        this.setState({
          offset: this.props.templates.length,
        })
      );
    };

    setRef = (ref) => (this.table = ref);

    handleClearSearch = (setFieldValue) => {
      this.setState(
        {
          filtersActive: false,
          filters: {
            search: '',
            inspectionTypes: Array(inspectionTypes.length).fill(false),
          },
          offset: 0,
        },
        () => this.fetchTemplates()
      );

      if (this.table) this.table.scrollToTop(0);
      setFieldValue('search', '');
      setFieldValue('inspectionTypes', Array(inspectionTypes.length).fill(false));
    };

    handleFilters = (values) => {
      const isFiltersUntouched = values.search === '' && !values.inspectionTypes.includes(true);
      const newState = {
        filtersActive: !isFiltersUntouched,
        filters: {
          search: values.search,
          inspectionTypes: values.inspectionTypes,
        },
        drawerVisible: false,
        offset: 0,
      };

      this.setState(newState, () => this.fetchTemplates());
      if (this.table) this.table.scrollToTop(0);
    };

    handleSort = (key, order) => {
      this.setState(
        {
          sortKey: key,
          sortOrder: order,
          offset: 0,
        },
        () => {
          this.fetchTemplates();
        }
      );
      if (this.table) this.table.scrollToTop(0);
    };

    handleTabChange = (tabName) => {
      this.setState(
        {
          currentTab: tabName,
          offset: 0,
        },
        () => {
          this.fetchTemplates();
        }
      );
      if (this.table) this.table.scrollToTop(0);
    };

    handleCreateOrUpdateTemplate = (formData) => {
      return this.props.createOrUpdateTemplate(formData).then((res) => {
        this.props.getInspectionTemplatesStats();
        this.setState(
          {
            offset: 0,
          },
          () => {
            this.fetchTemplates();
          }
        );

        return res;
      });
    };

    columns = [
      {
        title: getTranslatedString('common.name'),
        dataKey: 'name',
        sortable: true,
        width: 150,
      },
      {
        title: getTranslatedString('common.description'),
        dataKey: 'description',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.inspections.inspectionType'),
        dataKey: 'inspection_type',
        sortable: true,
        width: 150,
      },
      {
        title: getTranslatedString('common.createdAt'),
        dataKey: 'created_at',
        sortable: true,
        width: 150,
        render: ({ data }) => {
          const date = moment(data).tz(this.timezone);
          if (date.isValid()) return date.locale(this.props.currentLanguage || 'en').fromNow();
          return '-';
        },
      },
      {
        title: getTranslatedString('landlordPortal.inspections.lastPerformedAt'),
        dataKey: 'last_performed_at',
        sortable: false,
        width: 150,
        render: ({ data }) => {
          const date = moment(data).tz(this.timezone);
          if (date.isValid()) return date.locale(this.props.currentLanguage || 'en').fromNow();
          return '-';
        },
      },
    ];

    render() {
      const { templates, templatesCounts } = this.props;
      const { filters } = this.state;

      const filterOptions = [
        {
          useSearch: false,
          options: inspectionTypes,
          name: 'inspectionTypes',
          title: getTranslatedString('landlordPortal.inspections.inspectionTypes'),
        },
      ];

      const tabs = [
        {
          title: getTranslatedString('common.active'),
          count: templatesCounts.active || 0,
          onClick: () => this.handleTabChange('active'),
        },
        {
          title: getTranslatedString('common.archived'),
          count: templatesCounts.archived || 0,
          onClick: () => this.handleTabChange('archived'),
        },
      ];

      return (
        <StyledMain>
          <StyledHeaderContainer>
            <StyledHeader>
              <HeaderWithPeriod
                headerText={getTranslatedString('landlordPortal.inspections.inspectionTemplates')}
                newStyle
              />
              {true ? ( // FEATURE TOGGLE FOR NEW INSPECTION TEMPLATE ADD DIALOG
                <StyledAddButton onClick={this.addTemplate}>
                  {getTranslatedString('landlordPortal.inspections.addTemplate')}
                </StyledAddButton>
              ) : (
                <AddTemplateDialog
                  handleCreateOrUpdateTemplate={this.handleCreateOrUpdateTemplate}
                  upsertingTemplate={this.props.upsertingTemplate}
                >
                  {({ toggle }) => (
                    <StyledAddButton onClick={toggle}>
                      {getTranslatedString('landlordPortal.inspections.addTemplate')}
                    </StyledAddButton>
                  )}
                </AddTemplateDialog>
              )}
            </StyledHeader>
            <TableFilters
              filters={filters}
              handleSubmitFilters={this.handleFilters}
              handleClearFilters={this.handleClearSearch}
              filterOptions={filterOptions}
            />
          </StyledHeaderContainer>

          <StyledTableContainer>
            {this._renderDesktopView({ templates, tabs })}
            {this._renderMobileView({ templates })}
            {this.renderDrawer()}
          </StyledTableContainer>
        </StyledMain>
      );
    }

    _renderDesktopView = ({ templates, tabs }) => (
      <MediaQuery query="(min-device-width: 750px)">
        <TabbedTable
          setRef={this.setRef}
          tabs={tabs}
          columns={this.columns}
          data={templates}
          searchResultsCount={this.props.templatesSearchResultsCount}
          loadMoreData={this.fetchTemplates}
          isLoading={this.props.fetchingInspectionTemplates}
          filtersActive={this.state.filtersActive}
          onRowClick={(data) => {
            this.openTemplate(data);
          }}
          onColumnClick={(data) => {
            this.handleSort(data.key, data.order);
          }}
        />
      </MediaQuery>
    );

    _renderMobileView = ({ templates }) => (
      <MediaQuery query="(max-device-width: 750px)">
        <MobileTable
          data={templates}
          columns={this.columns}
          title="name"
          stickyHeaderTop={this.state.filters.search ? '137px' : '107px'}
          isLoading={this.props.fetchingInspectionTemplates}
          fieldsAboveFold={[
            {
              title: getTranslatedString('common.description'),
              key: 'description',
            },
            {
              title: getTranslatedString('landlordPortal.inspections.inspectionType'),
              key: 'inspection_type',
            },
          ]}
          fieldsBelowFold={[
            {
              title: getTranslatedString('common.createdAt'),
              key: 'created_at',
            },
            {
              title: getTranslatedString('landlordPortal.inspections.lastPerformedAt'),
              key: 'last_performed_at',
            },
          ]}
          onRowClick={(data) => {
            this.openTemplate(data);
          }}
        />
      </MediaQuery>
    );

    renderDrawer = () => (
      <StyledDrawer open={this.state.drawerOpen} onClose={this.toggleDrawer} anchor="left">
        <TemplateDrawer
          toggleDrawer={this.toggleDrawer}
          data={this.state.selectedTemplate}
          createOrUpdateTemplate={this.handleCreateOrUpdateTemplate}
        />
      </StyledDrawer>
    );
  }
);
